import { z } from 'zod';

import {
  situationNeedsAdditionalCredits,
  situationNeedsCredits,
  situationNeedsDegree,
} from '../utils/setupOptions';

/**
 * Full wizard form state (all 4 steps). Per-situation required fields mirror the
 * backend `academic-info` validation; the `situationNeeds*` predicates come from
 * `setupOptions` so schema and UI stay in sync.
 */
export const wizardSchema = z
  .object({
    // Step 1 — Personal
    hasNameChange: z.boolean(),
    formerName: z.string().optional().nullable(),
    nameChangeReason: z.string().optional().nullable(),
    nameChangeDocumentId: z.number().int().positive().optional().nullable(),

    // Step 2 — Academic Situation
    priorEducationStatus: z.enum([
      'no_credits',
      'some_credits_no_associate',
      'many_credits_no_associate',
      'associate_only',
      'associate_plus_credits',
      'bachelor_or_higher',
    ]),

    // Step 3 — Credits Information
    targetDegreeType: z.enum(['associate', 'bachelor', 'master']),
    targetDegreeId: z.number().int().positive().optional().nullable(),
    estimatedCredits: z.number().int().nonnegative().optional().nullable(),
    creditSourceCount: z.number().int().positive().optional().nullable(),
    degreeName: z.string().optional().nullable(),
    degreeInstitution: z.string().optional().nullable(),
    additionalCredits: z.number().int().nonnegative().optional().nullable(),
    additionalCreditsSource: z
      .enum(['same_institution', 'different_institutions', 'both', 'not_sure'])
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    const missing = (field: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [field] });

    // Name change requires a reason when declared.
    if (data.hasNameChange && !data.formerName?.trim()) {
      missing('formerName', 'Enter your former name');
    }

    const s = data.priorEducationStatus;

    if (data.targetDegreeId == null) {
      missing('targetDegreeId', 'Select a target program');
    }

    if (situationNeedsCredits(s)) {
      if (data.estimatedCredits == null)
        missing('estimatedCredits', 'Enter your approximate credits');
      if (data.creditSourceCount == null)
        missing('creditSourceCount', 'Enter the number of credit sources');
    }

    if (situationNeedsDegree(s)) {
      if (!data.degreeName?.trim()) missing('degreeName', 'Enter your degree name');
      if (!data.degreeInstitution?.trim()) missing('degreeInstitution', 'Enter the institution');
    }

    if (situationNeedsAdditionalCredits(s)) {
      if (data.additionalCredits == null) missing('additionalCredits', 'Enter additional credits');
      if (!data.additionalCreditsSource)
        missing('additionalCreditsSource', 'Select where they were earned');
    }
  });

export type WizardValues = z.infer<typeof wizardSchema>;

export const wizardDefaultValues: WizardValues = {
  hasNameChange: false,
  formerName: null,
  nameChangeReason: null,
  nameChangeDocumentId: null,
  priorEducationStatus: 'no_credits',
  targetDegreeType: 'bachelor',
  targetDegreeId: null,
  estimatedCredits: null,
  creditSourceCount: null,
  degreeName: null,
  degreeInstitution: null,
  additionalCredits: null,
  additionalCreditsSource: null,
};

/** Field names that belong to each step, for per-step validation gating. */
export const STEP_FIELDS: Record<number, (keyof WizardValues)[]> = {
  0: ['hasNameChange', 'formerName', 'nameChangeReason', 'nameChangeDocumentId'],
  1: ['priorEducationStatus'],
  2: [
    'targetDegreeType',
    'targetDegreeId',
    'estimatedCredits',
    'creditSourceCount',
    'degreeName',
    'degreeInstitution',
    'additionalCredits',
    'additionalCreditsSource',
  ],
  3: [],
};
